/** Justificatifs : liste, envoi, téléchargement, corbeille. */

import { useRef, useState } from "react";
import { Link } from "react-router-dom";

import { ApiError, saveBlob } from "../api/client";
import { documentsApi } from "../api/endpoints";
import type { DocumentItem } from "../api/types";
import { Modal } from "../components/Modal";
import { useToast } from "../components/ToastContext";
import {
  Alert,
  Button,
  Card,
  EmptyState,
  ErrorAlert,
  LoadingBlock,
  PageHeader,
  SelectField,
  TextAreaField,
  TextField,
} from "../components/ui";
import {
  useDebounced,
  useDocumentTitle,
  useMutation,
  useResource,
} from "../hooks/useApi";
import {
  centsToEuros,
  eurosToCents,
  formatDate,
  formatFileSize,
  pluralize,
} from "../lib/format";

const PAGE_SIZE = 25;

/**
 * Natures de document proposees. Ces valeurs alimenteront le classement de la
 * GED prevue en V5 : les renseigner des maintenant evite d'avoir a reprendre
 * l'historique plus tard.
 */
const DOC_KINDS = [
  { value: "attestation", label: "Attestation" },
  { value: "receipt", label: "Reçu ou preuve de paiement" },
  { value: "invoice_sent", label: "Facture émise" },
  { value: "invoice_received", label: "Facture reçue" },
  { value: "letter", label: "Courrier administratif" },
  { value: "declaration", label: "Déclaration" },
  { value: "contract", label: "Contrat ou abonnement" },
  { value: "insurance", label: "Assurance" },
  { value: "bank", label: "Document bancaire" },
  { value: "tax", label: "Document fiscal" },
  { value: "legal", label: "Document juridique" },
  { value: "other", label: "Autre" },
];

export function DocumentsPage() {
  useDocumentTitle("Justificatifs");
  const toast = useToast();
  const fileInput = useRef<HTMLInputElement>(null);
  const [search, setSearch] = useState("");
  const debounced = useDebounced(search, 350);
  const [includeTrashed, setIncludeTrashed] = useState(false);
  const [offset, setOffset] = useState(0);
  const [editing, setEditing] = useState<DocumentItem | null>(null);
  const [form, setForm] = useState<Record<string, string>>({});

  const documents = useResource(
    () =>
      documentsApi.list({
        search: debounced.trim() || undefined,
        include_trashed: includeTrashed || undefined,
        limit: PAGE_SIZE,
        offset,
      }),
    [debounced, includeTrashed, offset],
  );

  const upload = useMutation((file: File) => documentsApi.upload(file), {
    onSuccess: async () => {
      await documents.reload();
      toast.success("Justificatif ajouté");
    },
    onError: (error) => toast.error("Envoi refusé", error.message),
  });

  const trash = useMutation((id: string) => documentsApi.trash(id), {
    onSuccess: async () => {
      await documents.reload();
      toast.success("Justificatif placé dans la corbeille");
    },
  });

  const restore = useMutation((id: string) => documentsApi.restore(id), {
    onSuccess: async () => {
      await documents.reload();
      toast.success("Justificatif restauré");
    },
  });

  const saveMetadata = useMutation(
    () => {
      if (!editing) throw new Error("Aucun justificatif sélectionné.");
      return documentsApi.update(editing.id, {
        display_name: form.display_name?.trim() || editing.display_name,
        doc_kind: form.doc_kind || null,
        document_date: form.document_date || null,
        expires_on: form.expires_on || null,
        retention_until: form.retention_until || null,
        issuer: form.issuer?.trim() || null,
        reference: form.reference?.trim() || null,
        amount_cents: eurosToCents(form.amount_euros ?? ""),
        amount_currency: form.amount_euros ? "EUR" : null,
        tags: form.tags
          ? form.tags
              .split(",")
              .map((tag) => tag.trim())
              .filter(Boolean)
          : null,
        notes: form.notes?.trim() || null,
      });
    },
    {
      onSuccess: async () => {
        setEditing(null);
        await documents.reload();
        toast.success("Justificatif mis à jour");
      },
      onError: (error) => toast.error("Enregistrement refusé", error.message),
    },
  );

  const openEditor = (document: DocumentItem) => {
    setEditing(document);
    setForm({
      display_name: document.display_name,
      doc_kind: document.doc_kind ?? "",
      document_date: document.document_date ?? "",
      expires_on: document.expires_on ?? "",
      retention_until: document.retention_until ?? "",
      issuer: document.issuer ?? "",
      reference: document.reference ?? "",
      amount_euros: centsToEuros(document.amount_cents),
      tags: (document.tags ?? []).join(", "),
      notes: document.notes ?? "",
    });
  };

  const setField = (key: string, value: string) =>
    setForm((current) => ({ ...current, [key]: value }));

  const download = async (id: string) => {
    try {
      saveBlob(await documentsApi.download(id));
    } catch (error) {
      toast.error(
        "Téléchargement impossible",
        error instanceof ApiError ? error.message : undefined,
      );
    }
  };

  return (
    <>
      <PageHeader
        title="Justificatifs"
        subtitle={
          documents.data
            ? `${pluralize(documents.data.total, "fichier")} conservé${
                documents.data.total > 1 ? "s" : ""
              }.`
            : undefined
        }
        actions={
          <>
            <input
              ref={fileInput}
              type="file"
              className="visually-hidden"
              onChange={(event) => {
                const file = event.target.files?.[0];
                if (file) void upload.run(file);
                event.target.value = "";
              }}
            />
            <Button
              variant="primary"
              loading={upload.pending}
              onClick={() => fileInput.current?.click()}
            >
              Ajouter un fichier
            </Button>
          </>
        }
      />

      <Alert tone="info" title="Vos fichiers ne sont pas accessibles publiquement">
        Ils sont stockés hors de toute arborescence web, sous un nom non
        prédictible, et chaque téléchargement vérifie vos droits côté serveur.
      </Alert>

      <div className="filters mt-4">
        <div className="filters__wide">
          <TextField
            label="Rechercher"
            type="search"
            placeholder="Nom du justificatif"
            value={search}
            onChange={(event) => {
              setSearch(event.target.value);
              setOffset(0);
            }}
          />
        </div>
        <div className="field">
          <span className="field__label">Corbeille</span>
          <Button
            variant={includeTrashed ? "primary" : "ghost"}
            onClick={() => {
              setIncludeTrashed(!includeTrashed);
              setOffset(0);
            }}
            aria-pressed={includeTrashed}
          >
            {includeTrashed ? "Masquer les éléments supprimés" : "Afficher la corbeille"}
          </Button>
        </div>
      </div>

      <ErrorAlert error={documents.error} title="Liste indisponible" />
      <ErrorAlert error={upload.error} title="Envoi refusé" />

      <Card flush>
        {documents.loading ? (
          <LoadingBlock />
        ) : !documents.data || documents.data.items.length === 0 ? (
          <EmptyState title="Aucun justificatif">
            Ajoutez une attestation, un reçu, un courrier ou une preuve de
            paiement. Formats acceptés : PDF, images, documents bureautiques,
            texte, CSV, ZIP (25 Mo maximum).
          </EmptyState>
        ) : (
          <div className="table-wrap">
            <table className="data">
              <caption className="visually-hidden">Liste des justificatifs</caption>
              <thead>
                <tr>
                  <th scope="col">Nom</th>
                  <th scope="col">Ajouté le</th>
                  <th scope="col">Taille</th>
                  <th scope="col">Démarches liées</th>
                  <th scope="col">Actions</th>
                </tr>
              </thead>
              <tbody>
                {documents.data.items.map((document) => (
                  <tr key={document.id}>
                    <td>
                      {document.display_name}
                      {document.is_trashed && (
                        <>
                          {" "}
                          <span className="badge badge--warning">Corbeille</span>
                        </>
                      )}
                      {document.doc_kind && (
                        <>
                          {" "}
                          <span className="badge">
                            {DOC_KINDS.find((k) => k.value === document.doc_kind)
                              ?.label ?? document.doc_kind}
                          </span>
                        </>
                      )}
                      {document.expires_on && (
                        <>
                          {" "}
                          <span className="badge badge--warning">
                            Expire le {formatDate(document.expires_on)}
                          </span>
                        </>
                      )}
                      <div className="inline-note mono">{document.mime_type}</div>
                    </td>
                    <td className="nowrap">{formatDate(document.created_at)}</td>
                    <td className="numeric nowrap">
                      {formatFileSize(document.size_bytes)}
                    </td>
                    <td>
                      {document.linked_task_ids.length === 0 ? (
                        <span className="text-muted">—</span>
                      ) : (
                        document.linked_task_ids.map((id) => (
                          <div key={id}>
                            <Link to={`/demarches/${id}`}>Voir la démarche</Link>
                          </div>
                        ))
                      )}
                    </td>
                    <td>
                      <div className="btn-group">
                        <Button
                          size="sm"
                          variant="quiet"
                          onClick={() => void download(document.id)}
                        >
                          Télécharger
                        </Button>
                        {!document.is_trashed && (
                          <Button
                            size="sm"
                            variant="quiet"
                            onClick={() => openEditor(document)}
                          >
                            Modifier
                          </Button>
                        )}
                        {document.is_trashed ? (
                          <Button
                            size="sm"
                            variant="quiet"
                            onClick={() => void restore.run(document.id)}
                          >
                            Restaurer
                          </Button>
                        ) : (
                          <Button
                            size="sm"
                            variant="quiet"
                            onClick={() => void trash.run(document.id)}
                          >
                            Supprimer
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <Modal
        open={editing !== null}
        title="Informations du justificatif"
        description={
          <p>
            Ces informations servent au classement et aux alertes. Renseignez
            une date d'expiration pour être prévenu avant l'échéance d'un
            contrat ou d'une assurance.
          </p>
        }
        onClose={() => setEditing(null)}
        actions={
          <Button
            variant="primary"
            loading={saveMetadata.pending}
            onClick={() => void saveMetadata.run()}
          >
            Enregistrer
          </Button>
        }
      >
        <ErrorAlert error={saveMetadata.error} title="Enregistrement refusé" />
        <TextField
          label="Nom affiché"
          value={form.display_name ?? ""}
          onChange={(event) => setField("display_name", event.target.value)}
        />
        <SelectField
          label="Nature du document"
          options={DOC_KINDS}
          placeholder="Non précisée"
          value={form.doc_kind ?? ""}
          onChange={(event) => setField("doc_kind", event.target.value)}
        />
        <TextField
          label="Date du document"
          type="date"
          optional
          value={form.document_date ?? ""}
          onChange={(event) => setField("document_date", event.target.value)}
        />
        <TextField
          label="Date d'expiration"
          type="date"
          optional
          value={form.expires_on ?? ""}
          onChange={(event) => setField("expires_on", event.target.value)}
          hint="Échéance d'un contrat, d'une assurance ou d'une attestation."
        />
        <TextField
          label="À conserver jusqu'au"
          type="date"
          optional
          value={form.retention_until ?? ""}
          onChange={(event) => setField("retention_until", event.target.value)}
          hint="Les pièces comptables se conservent 10 ans."
        />
        <TextField
          label="Émetteur"
          optional
          value={form.issuer ?? ""}
          onChange={(event) => setField("issuer", event.target.value)}
          hint="Urssaf, service des impôts, assureur, fournisseur…"
        />
        <TextField
          label="Référence"
          optional
          value={form.reference ?? ""}
          onChange={(event) => setField("reference", event.target.value)}
        />
        <TextField
          label="Montant en euros"
          optional
          inputMode="decimal"
          value={form.amount_euros ?? ""}
          onChange={(event) => setField("amount_euros", event.target.value)}
        />
        <TextField
          label="Étiquettes"
          optional
          value={form.tags ?? ""}
          onChange={(event) => setField("tags", event.target.value)}
          hint="Séparées par des virgules."
        />
        <TextAreaField
          label="Notes"
          optional
          value={form.notes ?? ""}
          onChange={(event) => setField("notes", event.target.value)}
        />
      </Modal>

      {documents.data && documents.data.total > PAGE_SIZE && (
        <nav className="row row--between mt-4" aria-label="Pagination">
          <Button
            variant="ghost"
            disabled={offset === 0}
            onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}
          >
            Page précédente
          </Button>
          <span className="inline-note">
            {offset + 1}–{Math.min(offset + PAGE_SIZE, documents.data.total)} sur{" "}
            {documents.data.total}
          </span>
          <Button
            variant="ghost"
            disabled={!documents.data.has_more}
            onClick={() => setOffset(offset + PAGE_SIZE)}
          >
            Page suivante
          </Button>
        </nav>
      )}
    </>
  );
}
